import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { TableHead, TableWrapper, FilterRow } from '../components/TableWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { WithWrite } from '../lib/rbac';
import { suppliersApi, type Supplier, type SupplierFormData } from '../services/suppliers';

const EMPTY_FORM: SupplierFormData = {
  name: '',
  isCompany: null,
  orgNr: '',
  vatNr: '',
  email: '',
  phone: '',
  website: '',
  address1: '',
  address2: '',
  postalCode: '',
  city: '',
  country: 'Sverige',
  latitude: '',
  longitude: '',
  radiusM: null,
  notes: '',
  isActive: true,
};

export function SuppliersPage() {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [editing, setEditing] = useState<Supplier | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<SupplierFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const { canRead, canWrite } = useAuth();

  if (!canRead('suppliers')) {
    return <Navigate to="/" replace />;
  }

  const loadSuppliers = useCallback(async () => {
    try {
      const response = await suppliersApi.list();
      setSuppliers(response.suppliers);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte hämta leverantörer');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadSuppliers();
  }, [loadSuppliers]);

  const resetForm = () => {
    setEditing(null);
    setShowForm(false);
    setForm(EMPTY_FORM);
  };

  const filteredSuppliers = useMemo(
    () =>
      suppliers.filter((supplier) => {
        const matchesText =
          !filter ||
          supplier.name.toLowerCase().includes(filter.toLowerCase()) ||
          (supplier.city ?? '').toLowerCase().includes(filter.toLowerCase()) ||
          String(supplier.id).includes(filter);

        const matchesStatus =
          statusFilter === 'all' ||
          (statusFilter === 'active' ? !!supplier.isActive : !supplier.isActive);

        return matchesText && matchesStatus;
      }),
    [filter, statusFilter, suppliers]
  );

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = async (supplier: Supplier) => {
    try {
      const detail = await suppliersApi.get(supplier.id);
      setEditing(detail);
      setForm({
        name: detail.name,
        isCompany: detail.isCompany === null ? null : !!detail.isCompany,
        orgNr: detail.orgNr ?? '',
        vatNr: detail.vatNr ?? '',
        email: detail.email ?? '',
        phone: detail.phone ?? '',
        website: detail.website ?? '',
        address1: detail.address1 ?? '',
        address2: detail.address2 ?? '',
        postalCode: detail.postalCode ?? '',
        city: detail.city ?? '',
        country: detail.country ?? '',
        latitude: detail.latitude ?? '',
        longitude: detail.longitude ?? '',
        radiusM: detail.radiusM ?? null,
        notes: detail.notes ?? '',
        isActive: !!detail.isActive,
      });
      setShowForm(true);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda leverantör');
    }
  };

  const handleSave = async () => {
    if (!form.name?.trim()) {
      toast.error('Namn krävs');
      return;
    }

    setSaving(true);
    try {
      if (editing) {
        await suppliersApi.update(editing.id, form);
        toast.success('Leverantör uppdaterad');
      } else {
        await suppliersApi.create(form);
        toast.success('Leverantör skapad');
      }
      resetForm();
      await loadSuppliers();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte spara leverantör');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (supplier: Supplier) => {
    try {
      await suppliersApi.delete(supplier.id);
      toast.success('Leverantör inaktiverad');
      if (editing?.id === supplier.id) {
        resetForm();
      }
      await loadSuppliers();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte inaktivera leverantör');
    }
  };

  if (loading) {
    return <p className="text-sm text-gray-500">Laddar...</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-lg font-semibold">Leverantörer</h1>
        <WithWrite moduleCode="suppliers">
          <button
            type="button"
            onClick={openCreate}
            className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover"
          >
            Ny leverantör
          </button>
        </WithWrite>
      </div>

      <div className="flex flex-wrap gap-2">
        <input
          type="text"
          placeholder="Filtrera på id, namn eller ort"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          className="w-72"
        />
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="w-40">
          <option value="active">Aktiva</option>
          <option value="inactive">Inaktiva</option>
          <option value="all">Alla</option>
        </select>
      </div>

      <TableWrapper>
        <TableHead>
          <tr>
            <th className="px-2 py-1.5 text-left font-medium">Id</th>
            <th className="px-2 py-1.5 text-left font-medium">Namn</th>
            <th className="px-2 py-1.5 text-left font-medium">Ort</th>
            <th className="px-2 py-1.5 text-left font-medium">Telefon</th>
            <th className="px-2 py-1.5 text-left font-medium">E-post</th>
            <th className="px-2 py-1.5 text-left font-medium">Status</th>
            <WithWrite moduleCode="suppliers">
              <th className="px-2 py-1.5 text-left font-medium">Åtgärder</th>
            </WithWrite>
          </tr>
          <FilterRow>
            <td colSpan={canWrite('suppliers') ? 7 : 6} className="px-2 py-1" />
          </FilterRow>
        </TableHead>
        <tbody>
          {filteredSuppliers.map((supplier) => (
            <tr key={supplier.id} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="px-2 py-1">{supplier.id}</td>
              <td className="px-2 py-1 font-medium">{supplier.name}</td>
              <td className="px-2 py-1">{supplier.city ?? '-'}</td>
              <td className="px-2 py-1">{supplier.phone ?? '-'}</td>
              <td className="px-2 py-1">{supplier.email ?? '-'}</td>
              <td className="px-2 py-1">{supplier.isActive ? 'Aktiv' : 'Inaktiv'}</td>
              <WithWrite moduleCode="suppliers">
                <td className="px-2 py-1">
                  <button type="button" onClick={() => openEdit(supplier)} className="mr-2 text-primary hover:underline">
                    Redigera
                  </button>
                  {supplier.isActive ? (
                    <button
                      type="button"
                      onClick={() => handleDeactivate(supplier)}
                      className="text-red-600 hover:underline"
                    >
                      Inaktivera
                    </button>
                  ) : null}
                </td>
              </WithWrite>
            </tr>
          ))}
        </tbody>
      </TableWrapper>

      {filteredSuppliers.length === 0 && <p className="text-sm text-gray-500">Inga leverantörer eller inga träffar.</p>}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded bg-white p-4 shadow-lg">
            <div className="space-y-3">
              <h2 className="text-base font-semibold">{editing ? 'Redigera leverantör' : 'Ny leverantör'}</h2>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="block text-sm text-gray-600">Namn *</label>
                  <input
                    value={form.name}
                    onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Typ</label>
                  <select
                    value={form.isCompany === null ? '' : form.isCompany ? 'company' : 'private'}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        isCompany:
                          event.target.value === '' ? null : event.target.value === 'company',
                      }))
                    }
                    className="w-full"
                  >
                    <option value="">Okänt</option>
                    <option value="company">Företag</option>
                    <option value="private">Privatperson</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Organisationsnummer</label>
                  <input
                    value={form.orgNr ?? ''}
                    onChange={(event) => setForm((current) => ({ ...current, orgNr: event.target.value }))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">VAT-nummer</label>
                  <input
                    value={form.vatNr ?? ''}
                    onChange={(event) => setForm((current) => ({ ...current, vatNr: event.target.value }))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">E-post</label>
                  <input
                    value={form.email ?? ''}
                    onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Telefon</label>
                  <input
                    value={form.phone ?? ''}
                    onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Webbplats</label>
                  <input
                    value={form.website ?? ''}
                    onChange={(event) => setForm((current) => ({ ...current, website: event.target.value }))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Land</label>
                  <input
                    value={form.country ?? ''}
                    onChange={(event) => setForm((current) => ({ ...current, country: event.target.value }))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Adress 1</label>
                  <input
                    value={form.address1 ?? ''}
                    onChange={(event) => setForm((current) => ({ ...current, address1: event.target.value }))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Adress 2</label>
                  <input
                    value={form.address2 ?? ''}
                    onChange={(event) => setForm((current) => ({ ...current, address2: event.target.value }))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Postnummer</label>
                  <input
                    value={form.postalCode ?? ''}
                    onChange={(event) => setForm((current) => ({ ...current, postalCode: event.target.value }))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Ort</label>
                  <input
                    value={form.city ?? ''}
                    onChange={(event) => setForm((current) => ({ ...current, city: event.target.value }))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Latitud</label>
                  <input
                    value={form.latitude ?? ''}
                    onChange={(event) => setForm((current) => ({ ...current, latitude: event.target.value }))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Longitud</label>
                  <input
                    value={form.longitude ?? ''}
                    onChange={(event) => setForm((current) => ({ ...current, longitude: event.target.value }))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Radie (meter)</label>
                  <input
                    type="number"
                    min={0}
                    value={form.radiusM ?? ''}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        radiusM: event.target.value === '' ? null : Number(event.target.value),
                      }))
                    }
                    className="w-full"
                  />
                </div>
                {editing && (
                  <label className="flex items-center gap-2 rounded border border-gray-200 px-3 py-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!form.isActive}
                      onChange={(event) => setForm((current) => ({ ...current, isActive: event.target.checked }))}
                    />
                    Aktiv
                  </label>
                )}
              </div>

              <div>
                <label className="block text-sm text-gray-600">Anteckningar</label>
                <textarea
                  value={form.notes ?? ''}
                  onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
                  className="min-h-[110px] w-full"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover disabled:opacity-50"
                >
                  {saving ? 'Sparar...' : 'Spara'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-100"
                >
                  Avbryt
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
